import React, { useMemo } from 'react';
import { format, parseISO, isToday, addDays, compareAsc } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Phone, Calendar, Play, CheckCircle, Navigation } from 'lucide-react';
import { motion } from 'framer-motion';
import type { Assignment } from '@shared/types';
import toast from 'react-hot-toast';

interface MobileAgendaProps {
    assignments: Assignment[];
    onSelectAssignment: (assignment: Assignment) => void;
    onStartWorksite?: (id: string) => void;
    onFinishWorksite?: (id: string) => void;
}

export const MobileAgenda: React.FC<MobileAgendaProps> = ({
    assignments,
    onSelectAssignment,
    onStartWorksite,
    onFinishWorksite
}) => {

    const agendaItems = useMemo(() => {
        const items: { date: Date; assignment: Assignment }[] = [];

        assignments.forEach(assignment => {
            try {
                const start = parseISO(assignment.startDate);
                const end = parseISO(assignment.endDate);

                // iter days
                let current = start;
                while (current <= end) {
                    items.push({ date: current, assignment });
                    current = addDays(current, 1);
                }
            } catch (e) {
                console.error("Invalid assignment date", assignment);
            }
        });

        // Sort by date
        items.sort((a, b) => compareAsc(a.date, b.date));

        // Group by date string key
        const grouped = new Map<string, { date: Date; assignments: Assignment[] }>();

        items.forEach(item => {
            const key = format(item.date, 'yyyy-MM-dd');
            if (!grouped.has(key)) {
                grouped.set(key, { date: item.date, assignments: [] });
            }
            const group = grouped.get(key)!;
            if (!group.assignments.find(a => a.id === item.assignment.id)) {
                group.assignments.push(item.assignment);
            }
        });

        return Array.from(grouped.values());
    }, [assignments]);

    const handleAction = (e: React.MouseEvent, type: 'start' | 'finish' | 'call' | 'navigate', assignment: Assignment) => {
        e.stopPropagation();

        if (type === 'start' && onStartWorksite) {
            onStartWorksite(assignment.id);
            toast.success("Intervention démarrée");
        }
        if (type === 'finish' && onFinishWorksite) {
            onFinishWorksite(assignment.id);
            toast.success("Intervention terminée");
        }
        if (type === 'call' && assignment.contactPhone) {
            window.location.href = `tel:${assignment.contactPhone}`;
        }
        if (type === 'navigate' && assignment.address) {
            // Open Google Maps
            const query = encodeURIComponent(`${assignment.address} ${assignment.city || ''}`);
            window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
        }
    };

    if (agendaItems.length === 0) {
        return (
            <div className="glass-panel p-8 text-center min-h-[50vh] flex flex-col items-center justify-center">
                <Calendar className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Aucun chantier prévu</p>
                <p className="text-gray-600 text-sm mt-2">Profitez de votre temps libre !</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-32 px-4 pt-4">
            {agendaItems.map((group, groupIndex) => (
                <motion.div
                    key={group.date.toISOString()}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: groupIndex * 0.05 }}
                    className="relative pl-14"
                >
                    {/* Timeline Line */}
                    <div className="absolute left-[29px] top-12 bottom-0 w-[2px] bg-gradient-to-b from-white/10 to-transparent" />

                    {/* Date Header - Floating on left */}
                    <div className="absolute left-0 top-1 flex flex-col items-center w-14 sticky top-20">
                        <span className={`text-2xl font-bold tracking-tight ${isToday(group.date) ? 'text-primary drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]' : 'text-gray-200'}`}>
                            {format(group.date, 'dd')}
                        </span>
                        <div className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isToday(group.date) ? 'bg-primary text-white' : 'text-gray-500'}`}>
                            {format(group.date, 'EEE', { locale: fr })}
                        </div>
                    </div>

                    {/* Assignments List for the Day */}
                    <div className="space-y-6">
                        {group.assignments.map((assignment) => {
                            const rawStatus = assignment.status || 'pending';
                            const status = rawStatus.toLowerCase().trim();
                            const isDone = status === 'completed-by-tech' || status === 'done' || status === 'closed';
                            const isInProgress = status === 'in-progress' || status === 'in_progress';

                            // Visuals
                            const cardOpacity = isDone ? 'opacity-70' : 'opacity-100';
                            const cardBorder = isInProgress ? 'border-primary/50 shadow-[0_0_15px_-3px_rgba(59,130,246,0.3)]' : 'border-white/5';

                            return (
                                <div key={`${assignment.id}-${group.date.toISOString()}`} className="relative group/card">
                                    {/* Timeline Dot */}
                                    <div className={`absolute -left-[30px] top-8 w-4 h-4 rounded-full border-4 border-[#0f172a] z-10 
                                        ${isDone ? 'bg-gray-600' : isInProgress ? 'bg-orange-500 animate-pulse' : 'bg-primary'}
                                    `} />

                                    <div
                                        onClick={() => onSelectAssignment(assignment)}
                                        className={`
                                            glass-panel p-0 relative overflow-hidden active:scale-[0.98] transition-all duration-300
                                            ${cardBorder} ${cardOpacity} bg-[#1e293b]/60 backdrop-blur-xl
                                        `}
                                    >
                                        {/* Status Bar */}
                                        <div className={`h-1.5 w-full ${isDone ? 'bg-green-500/50' : isInProgress ? 'bg-orange-500' : 'bg-primary'}`} />

                                        <div className="p-5">
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className="font-bold text-white text-lg leading-tight pr-4">{assignment.clientName}</h3>
                                                {assignment.startTime && (
                                                    <span className="text-xs font-mono bg-black/30 px-2 py-1 rounded text-gray-300 border border-white/5">
                                                        {assignment.startTime}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-1.5 text-sm text-gray-400 mb-4">
                                                {assignment.city && (
                                                    <div className="flex items-center gap-2">
                                                        <MapPin size={14} className="text-gray-500" />
                                                        <span className="truncate">{assignment.address} {assignment.city && `, ${assignment.city}`}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Quick Actions Row */}
                                            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/5">

                                                {/* Primary Operational Action */}
                                                {!isDone && (
                                                    isInProgress ? (
                                                        <button
                                                            onClick={(e) => handleAction(e, 'finish', assignment)}
                                                            className="flex-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold transition-colors"
                                                        >
                                                            <CheckCircle size={16} /> Terminer
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => handleAction(e, 'start', assignment)}
                                                            className="flex-1 bg-primary/20 hover:bg-primary/30 text-primary-400 border border-primary/50 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold transition-colors"
                                                        >
                                                            <Play size={16} fill="currentColor" /> Démarrer
                                                        </button>
                                                    )
                                                )}

                                                {/* Completed State */}
                                                {isDone && (
                                                    <div className="flex-1 flex items-center justify-center gap-2 text-green-500/70 text-sm font-medium py-2 bg-green-500/5 rounded-lg border border-green-500/10">
                                                        <CheckCircle size={16} /> Terminé
                                                    </div>
                                                )}

                                                {/* Secondary Actions */}
                                                <div className="flex gap-2">
                                                    {assignment.contactPhone && (
                                                        <button
                                                            onClick={(e) => handleAction(e, 'call', assignment)}
                                                            className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10"
                                                        >
                                                            <Phone size={18} />
                                                        </button>
                                                    )}
                                                    {assignment.address && (
                                                        <button
                                                            onClick={(e) => handleAction(e, 'navigate', assignment)}
                                                            className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-blue-300 border border-white/10"
                                                        >
                                                            <Navigation size={18} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>
            ))}
        </div>
    );
};
